/**
 * TwitterConnectionStatus
 * 
 * Displays status of connected Twitter accounts.
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, X, RefreshCw, Unplug, Loader2, Lock, Share2, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import { EmptyState } from '../ui/empty-state';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Twitter SVG Icon
const TwitterIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function TwitterConnectionStatus({ onRefresh }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { toast } = useToast();

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/social/twitter/accounts`, {
        withCredentials: true
      });

      if (response.data.success) {
        setAccounts(response.data.data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les comptes Twitter',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleRefresh = async (tokenId) => {
    setActionLoading(tokenId);
    try {
      const response = await axios.post(
        `${API_URL}/api/social/twitter/refresh?token_id=${tokenId}`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast({
          title: 'Succès',
          description: 'Métadonnées rafraîchies',
        });
        fetchAccounts();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Échec du rafraîchissement',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (tokenId, accountName) => {
    if (!window.confirm(`Déconnecter ${accountName} ?`)) return;

    setActionLoading(tokenId);
    try {
      const response = await axios.post(
        `${API_URL}/api/social/twitter/disconnect?token_id=${tokenId}`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast({
          title: 'Succès',
          description: `${accountName} déconnecté`,
        });
        fetchAccounts();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Échec de la déconnexion',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="twitter-status-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={<Lock size={48} />}
        title="Connectez Twitter/X 🐦"
        message="Publiez vos tweets, images et vidéos directement depuis NEXORA AI. Planifiez votre contenu et suivez vos performances en un seul endroit."
        primaryAction={{
          label: "Connecter Twitter/X",
          onClick: () => window.location.href = '/social-accounts',
          icon: <Share2 size={20} />
        }}
        secondaryAction={{
          label: "Pourquoi OAuth 2.0 ?",
          onClick: () => {
            toast({
              title: "Sécurité OAuth 2.0",
              description: "Vos identifiants Twitter restent privés. NEXORA AI utilise OAuth 2.0 + PKCE pour une sécurité maximale.",
              variant: "default"
            });
          },
          variant: "outline",
          icon: <HelpCircle size={20} />
        }}
        variant="info"
        size="default"
        animated={true}
        testId="empty-twitter"
        footer={
          <p className="text-sm text-zinc-500">
            🔒 OAuth 2.0 + PKCE — Standard de sécurité Twitter
          </p>
        }
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="twitter-status-list">
      {accounts.map((account) => {
        const isExpired = !account.is_valid;
        const isLoading = actionLoading === account.token_id;

        return (
          <Card
            key={account.token_id}
            className="bg-zinc-800/50 border-zinc-700 p-4"
            data-testid={`twitter-account-${account.token_id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-600/20 rounded-lg">
                  <TwitterIcon className="w-6 h-6 text-blue-500" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-base font-semibold text-white">
                      {account.account_name}
                    </h4>
                    {account.is_valid ? (
                      <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                        <Check className="w-3 h-3 mr-1" />
                        Actif
                      </Badge>
                    ) : (
                      <Badge className="bg-red-600/20 text-red-400 border-red-600/30">
                        <X className="w-3 h-3 mr-1" />
                        Expiré
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-zinc-400">
                    ID: {account.account_id}
                  </p>
                  
                  <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                    <span>Connecté: {new Date(account.connected_at).toLocaleDateString('fr-FR')}</span>
                    {account.expires_at && (
                      <span>Expire: {new Date(account.expires_at).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleRefresh(account.token_id)}
                  disabled={isLoading}
                  size="sm"
                  variant="ghost"
                  className="text-zinc-400 hover:text-white"
                  data-testid={`refresh-twitter-${account.token_id}`}
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
                
                <Button
                  onClick={() => handleDisconnect(account.token_id, account.account_name)}
                  disabled={isLoading}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                  data-testid={`disconnect-twitter-${account.token_id}`}
                >
                  <Unplug className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {isExpired && (
              <div className="mt-3 p-3 bg-red-600/10 border border-red-600/20 rounded-lg">
                <p className="text-sm text-red-400">
                  ⚠️ Ce token a expiré. Reconnectez votre compte Twitter.
                </p>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
